import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardBody, Table, Spinner, Button, Modal, ModalHeader, ModalBody } from "reactstrap"
import apiClient from "helpers/apiClient"
import { toast } from "react-toastify"

const formatBuyerTimeOfClosure = (v?: string | null) => {
  const s = String(v ?? "").trim()
  if (!s) return ""
  if (/^\d+$/.test(s)) return `${s} Days`
  const monthsMatch = s.match(/^(\d+)_months$/)
  if (monthsMatch?.[1]) return `${monthsMatch[1]} Months`
  return s
}

export interface ClosedBiddingTopBid {
  bidId?: string
  rank: number
  amount: number
  amountFormatted: string
  bidderName: string
  bidderEmail: string
  createdAtFormatted: string
  buyerTimeOfClosure?: string | null
  closureDays?: number | null
  closureDueDateFormatted?: string | null
}

export interface ClosedBiddingRow {
  listingId: string
  propertyTitle: string
  city?: string
  state?: string
  totalSalePrice?: number
  auctionEndDateFormatted: string
  topBids: ClosedBiddingTopBid[]
}

const ClosedBiddingTable = () => {
  const [rows, setRows] = useState<ClosedBiddingRow[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<ClosedBiddingRow | null>(null)
  const [sendingEmail, setSendingEmail] = useState(false)

  useEffect(() => {
    apiClient
      .get<{ success: boolean; data?: ClosedBiddingRow[] }>("/api/listings/closed-bidding")
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) setRows(res.data.data)
        else setRows([])
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  const openBuyers = (row: ClosedBiddingRow) => {
    setSelectedRow(row)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedRow(null)
  }

  const sendWinnerEmail = (bid: ClosedBiddingTopBid) => {
    if (!selectedRow?.listingId || !bid?.bidId) return
    setSendingEmail(true)
    apiClient
      .post(`/api/listings/closed-bidding/${selectedRow.listingId}/notify-winner`, { bidId: bid.bidId })
      .then((res) => {
        if (res.data?.success) {
          toast.success(res.data.message || "Email sent.")
        } else {
          toast.error(res.data?.message || "Failed to send email.")
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to send email.")
      })
      .finally(() => setSendingEmail(false))
  }

  if (loading) {
    return (
      <Card>
        <CardBody>
          <h4 className="card-title mb-4">Closed Bidding – All Bidders</h4>
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        </CardBody>
      </Card>
    )
  }

  if (rows.length === 0) {
    return (
      <Card>
        <CardBody>
          <h4 className="card-title mb-4">Closed Bidding – All Bidders</h4>
          <p className="text-muted mb-0">No closed auctions for your listings yet. When an auction ends, all bidders will appear here.</p>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardBody>
          <h4 className="card-title mb-4">Closed Bidding – All Bidders</h4>
          <p className="text-muted small mb-3">Properties whose bidding has ended. Admin/Super-admin see all; Broker/Seller see only their listings. Click &quot;View buyers&quot; to see all bidders and send winner email.</p>
          <div className="table-responsive">
            <Table className="align-middle table-centered table-nowrap mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 50 }}>S.No.</th>
                  <th style={{ width: 140 }}>Action</th>
                  <th>Property</th>
                  <th>Auction ended</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.listingId}>
                    <td>{idx + 1}</td>
                    <td>
                      <Button color="primary" size="sm" onClick={() => openBuyers(row)}>
                        View buyers
                      </Button>
                    </td>
                    <td>
                      <Link to={`/property/${row.listingId}`} className="text-primary fw-medium">
                        {row.propertyTitle}
                      </Link>
                      {(row.city || row.state) && (
                        <div className="text-muted small">{[row.city, row.state].filter(Boolean).join(", ")}</div>
                      )}
                    </td>
                    <td>{row.auctionEndDateFormatted}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={closeModal} size="xl">
        <ModalHeader toggle={closeModal}>
          Buyers – {selectedRow?.propertyTitle ?? "—"}
        </ModalHeader>
        <ModalBody>
          {selectedRow && (
            <>
              <div className="mb-3">
                <strong>Auction ended:</strong>{" "}
                <span className="text-muted">{selectedRow.auctionEndDateFormatted}</span>
              </div>
              <div className="table-responsive">
                <Table className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Rank</th>
                      <th>Name</th>
                      <th className="text-end">Amount</th>
                      <th>Email</th>
                      <th>Deal closure</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRow.topBids.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">No bids</td>
                      </tr>
                    ) : (
                      selectedRow.topBids.map((b) => (
                        <tr key={b.rank}>
                          <td>
                            {b.rank === 1 ? (
                              <span className="badge bg-success">#1 Winner</span>
                            ) : (
                              `#${b.rank}`
                            )}
                          </td>
                          <td>{b.bidderName}</td>
                          <td className="text-end">{b.amountFormatted}</td>
                          <td>{b.bidderEmail}</td>
                          <td>
                            {b.buyerTimeOfClosure ? (
                              <span className="text-muted">
                                {formatBuyerTimeOfClosure(b.buyerTimeOfClosure)}
                                {b.closureDueDateFormatted ? ` (Due: ${b.closureDueDateFormatted})` : ""}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            <Button
                              color={b.rank === 1 ? "success" : "secondary"}
                              size="sm"
                              disabled={sendingEmail}
                              onClick={() => sendWinnerEmail(b)}
                            >
                              Send email
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="mt-3">
                <Link to={`/property/${selectedRow.listingId}`} className="btn btn-sm btn-outline-primary">
                  View property
                </Link>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

export default ClosedBiddingTable
